import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Flex, HStack } from '@chakra-ui/react';
import queryString from 'query-string';

import {
  isFetchingMore,
  isLoading,
  isSettingVariables,
} from 'common/graphql/networkStatus';
import { useGetInvoicesQuery } from 'common/hooks/useGetInvoicesQuery';
import Button from 'components/_core/Button';
import Chip from 'components/_core/Chip';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';
import { InvoiceModalsProvider } from 'features/Work/pages/Invoices/hooks/useInvoiceModals';
import { QuoteModalsProvider } from 'features/Work/pages/Quotes/hooks/useQuoteModals';

import {
  InvoiceListBody,
  QuoteListBody,
} from '../../../../../../components/WorkListBody';
import * as routes from '../../../../../../routes';
import { useGetQuotes } from '../../../../../hooks/useGetQuotes';
import locales from './locales';

interface ClientInvoicesAndQuotesProps {
  clientId: string;
}

const ClientInvoicesAndQuotes: FC<ClientInvoicesAndQuotesProps> = ({
  clientId,
}) => {
  const { search } = useLocation();
  const history = useHistory();
  const [isInvoicesActive, setIsInvoicesActive] = useState(true);

  const { quotes, ...quotesQuery } = useGetQuotes({ clientId });

  const { invoices, ...invoicesQuery } = useGetInvoicesQuery({
    filters: { clientId },
  });

  const handleShowInvoices = () => {
    history.replace({ search: '?tab=invoices' });
  };

  const handleHideInvoices = () => {
    history.replace({ search: '?tab=quotes' });
  };

  useEffect(() => {
    setIsInvoicesActive(queryString.parse(search)?.tab !== 'quotes');
  }, [search]);

  const loading =
    isLoading(quotesQuery.networkStatus) ||
    isSettingVariables(quotesQuery.networkStatus) ||
    isLoading(invoicesQuery.networkStatus) ||
    isSettingVariables(invoicesQuery.networkStatus);

  const onFetchMoreInvoices = () => {
    logEvent({
      name: 'Invoices More Loaded',
      properties: { from: 'ClientDetails' },
      usePrefix: false,
    });
    invoicesQuery.fetchMore();
  };

  const onFetchMoreQuotes = () => {
    logEvent({
      name: 'Quotes More Loaded',
      properties: { from: 'ClientDetails' },
      usePrefix: false,
    });
    quotesQuery.fetchMore();
  };

  return (
    <>
      <Flex
        justifyContent="space-between"
        marginBottom="space-32"
        marginTop="space-64"
      >
        <HStack>
          <Chip
            icon="receipt"
            isActive={isInvoicesActive}
            onClick={handleShowInvoices}
          >
            {locales.invoices}
          </Chip>
          <Chip
            icon="send"
            isActive={!isInvoicesActive}
            onClick={handleHideInvoices}
          >
            {locales.quotes}
          </Chip>
        </HStack>

        <SunshineDropdown>
          <SunshineDropdown.Trigger>
            <Button icon="chevron-down">{locales.add}</Button>
          </SunshineDropdown.Trigger>

          <SunshineDropdown.List>
            <SunshineDropdown.Item
              as={Link}
              icon="plus"
              onClick={() =>
                logEvent({
                  name: 'Add Invoice',
                  properties: { from: 'ClientDetailsPage' },
                })
              }
              to={`${routes.addInvoice}?clientId=${clientId}`}
            >
              {locales.createInvoice}
            </SunshineDropdown.Item>

            <SunshineDropdown.Item
              as={Link}
              icon="plus"
              onClick={() =>
                logEvent({
                  name: 'Add Quote',
                  properties: { from: 'ClientDetailsPage' },
                })
              }
              to={`${routes.addQuote}?clientId=${clientId}`}
            >
              {locales.createQuote}
            </SunshineDropdown.Item>
          </SunshineDropdown.List>
        </SunshineDropdown>
      </Flex>

      {loading ? (
        <Loader />
      ) : (
        <>
          {isInvoicesActive ? (
            <InvoiceModalsProvider>
              <InvoiceListBody
                hasNextPage={invoicesQuery.pageInfo?.hasNextPage ?? false}
                invoices={invoices}
                isFetchingMore={isFetchingMore(invoicesQuery.networkStatus)}
                onFetchMore={onFetchMoreInvoices}
              />
            </InvoiceModalsProvider>
          ) : (
            <QuoteModalsProvider>
              <QuoteListBody
                hasNextPage={quotesQuery.pageInfo?.hasNextPage ?? false}
                isFetchingMore={isFetchingMore(quotesQuery.networkStatus)}
                onFetchMore={onFetchMoreQuotes}
                quotes={quotes}
              />
            </QuoteModalsProvider>
          )}
        </>
      )}
    </>
  );
};

export default ClientInvoicesAndQuotes;
